import React, { useRef, useLayoutEffect, useEffect, useState, useContext } from 'react';
import { VideoContext } from '../context/VideoContext';
import { OverlayData } from '../data/Timing';
import useCanPlayThrough from '../hooks/useCanPlayThrough';
import useIsMobile from '../hooks/useIsMobile';
import DebugUtils from './DebugUtils';

import Header from './Header';
import VoiceOver from './VoiceOver';
import Intro from './Intro';
import IntroBackground from './IntroBackground';
import Overlays from './Overlays';
import DeepDives from './DeepDive/DeepDives';
import Outro from './Outro';

import ScrollButton from './ScrollButton';

import { gsap } from "gsap";
import { CrossFade } from './Utilities';
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const Video = () => {

  const { setPosition, setCurrentTime, setCurrentOverlay, isLoading, setIsLoading, setCurrentProgress, lastEvent, setLastEvent } = useContext(VideoContext);

  const [totalTime, setTotalTime] = useState(-1);   // total time of the video, this should be pulled from VideoContext. it's used in Overlays and DeepDives

  const beginRef = useRef(null);
  const continueRef = useRef(null);

  const headerRef = useRef(null);
  const introBgRef = useRef(null);
  const introScreenRef = useRef(null);
  const overlaysRef = useRef(null);
  const deepdivesRef = useRef(null);
  const outroRef = useRef(null);

  const isMobile = useIsMobile();
  const timeline = useRef(null);
  const stretchContainer = useRef(null);
  const videoRef = useRef(null);
  const debugRef = useRef(null);

  const [playIntroVideo, setPlayIntroVideo] = useState(false);
  const [playEndFrame, setPlayEndFrame] = useState(false);

  const canPlayThrough = useCanPlayThrough(videoRef);

  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    setTimeout(() => {
        const handleMouseDown = () => {
            setLastEvent('mousedown');
        };
        const sectionDivs = document.querySelectorAll(".section-div");
        sectionDivs.forEach((section, index) => {
            section.querySelectorAll(".tooltip").forEach(element => {
                element.dataset.sectionIndex = index;
            });
        });
        const handleFocus = (e) => {
            if (lastEvent !== 'mousedown') {
                let introHeight = document.querySelector("#IntroScreen").clientHeight;
                let stretch = document.querySelector("#Stretcher");
                let stretchHeight = stretch.clientHeight;
                let overlay = e.target.closest(".overlay-container");
                if (overlay) {
                    window.scrollTo({
                        top: (stretchHeight * parseFloat(overlay.dataset.timing)) + introHeight + 100,
                        left: 0,
                        behavior: "smooth",
                    });
                }
            }
            setLastEvent(null);  // Reset the lastEvent
        };
        const handleHeaderFocus = (e) => {
            if (lastEvent !== 'mousedown') {
                let introHeight = document.querySelector("#IntroScreen").clientHeight;
                let stretch = document.querySelector("#Stretcher");
                let outroScreen = document.querySelector("#outroScreen");
                let isElementInViewport = (el) => {
                    const rect = el.getBoundingClientRect();

                    const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
                    const windowWidth = (window.innerWidth || document.documentElement.clientWidth);
                
                    // Check for all edges of the element. If any edge is within the viewport, return true.
                    const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
                    const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);
                
                    return (vertInView && horInView);
                  }

                // Check if the element is in the viewport
                if (!isElementInViewport(stretch) && !isElementInViewport(outroScreen)) {
                    window.scrollTo({
                      top: introHeight + 100,
                      left: 0,
                      behavior: "smooth",
                    });
                }
            }
            setLastEvent(null);  // Reset the lastEvent
        };
        document.querySelectorAll(".tooltip, .deepDiveButton").forEach(element => {
            element.addEventListener('mousedown', handleMouseDown);
            element.addEventListener('focus', handleFocus, true);
        });
        document.querySelectorAll(".header-wrapper a").forEach(element => {
          element.addEventListener('mousedown', handleMouseDown);
          element.addEventListener('focus', handleHeaderFocus, true);
        });
    }, 250);
  }, []);

  // debug tool
  const [debugMode, setDebugMode] = useState(false);
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('debug') === "true") {
      setDebugMode(true);
      debugRef.current.classList.remove("hidden");
    }
  }, []);

  // builds the timeline that controls the video playback, and updates the VideoContext duration, progress, and direction (scroll direction)
  useEffect(() => {
    
    let totalProgress; // used to track how far through all the scroll triggers we are

    // mobile has to wait for canPlayThrough, desktop has to wait for metadataLoaded
    if (canPlayThrough && !hasInitialized) {
      
      // insert sections into stretchContainer to make the scrollTriggers that will control the video playback in sections (to get the pauses in between interstitials)
      (() => {

        // filter out all the elements that are not interstitials from the OverlayData array and put that into a new array, remove unnecessary properties
        const sectionTimes = OverlayData
          .filter(item => item.interstitial === true)
          .map(({ id, position, size, hold, interstitial, ...rest }) => rest);

        // add the very first one, technically it doesn't have a card, but it's the first interstitial
        const firstInterstitial = {
          content: <>First Interstitial</>,
          seconds: 0
        }
        sectionTimes.unshift(firstInterstitial);

        // set the duration of each element based on the starting time of the next element
        sectionTimes.forEach((item, index) => {  
          if (index < sectionTimes.length - 1) {
            item.duration = sectionTimes[index + 1].seconds - item.seconds;
          } else {
            item.duration = videoRef.current.duration - item.seconds;
          }
        });
        
        const totalBrowserHeight = stretchContainer.current.getBoundingClientRect().height;
      
        const sectionDivs = [];
        sectionTimes.forEach((sectionInfo, index) => {
  
          // create the new section and label it
          const section = document.createElement("section");
          section.classList.add("section-div");
          const paraEl = document.createElement("p");
          section.appendChild(paraEl);
          stretchContainer.current.appendChild(section);
          paraEl.innerHTML = `<div class="hidden">inertsitial ${index}</div>`;
          sectionDivs.push(section);

          if (index < sectionTimes.length - 1) {
            const spacer = document.createElement("div");
            spacer.classList.add("stretchSpacer");
            stretchContainer.current.appendChild(spacer);
          }          

          sectionInfo.percent = sectionInfo.duration / videoRef.current.duration;
          const height = `${totalBrowserHeight * sectionInfo.percent}px`;
          
          section.style.height = height;
        });
        
        sectionDivs.forEach((section, index) => {
          
          ScrollTrigger.create({
            trigger: section,
            start: "top",
            end: "bottom",
            id: "pin",
            onUpdate: self => {
              const progress = self.progress;
              const thisSectionInfo = sectionTimes[index];
              const sumOfPercents = sectionTimes.slice(0, index).reduce((sum, currentItem) => sum + currentItem.percent, 0);
              totalProgress = progress *  thisSectionInfo.percent + sumOfPercents;
              
              if (debugMode) debugRef.current.innerHTML = `time = ${(totalProgress * videoRef.current.duration).toFixed(2)} this section progress =  ${progress.toFixed(2)}, total progress = ${totalProgress.toFixed(2)}, sumOfPercents = ${sumOfPercents.toFixed(2)}`;
              
              timeline.current.progress(totalProgress);

              setPosition({
                duration: videoRef.current.duration,
                progress: totalProgress,
                direction: self.direction
              });
              
              setCurrentTime((totalProgress * videoRef.current.duration));
              setCurrentProgress(totalProgress);
              
            }
          });
        });

        stretchContainer.current.style.height = "fit-content";

      })();
      
      const buildTimeline = (videoDuration) => {
        return gsap.timeline({
          paused: true,
          defaults: { duration: 1 },
        });
      };

      setTotalTime(videoRef.current.duration);
      setIsLoading(false);

      // set the duration of the video for other components to generate timelines (Overlays & DeepDives)
      setPosition({
        duration: videoRef.current.duration,
      });

      timeline.current = gsap.timeline({ paused: true });   // is this necessary?  I think it's overwritten below

      const video = videoRef.current;

      if (video) {
        video.removeAttribute("autoplay");
        video.currentTime = 0;
        video.pause();  // this is necessary to get the video to play on mobile? sometimes video hiccoughs if you set currentTime berore a pause
        timeline.current = buildTimeline();
        timeline.current.fromTo(video, { currentTime: 0 }, { currentTime: video.duration || 1 });
      }

      setHasInitialized(true);
      console.log(`Video loaded, duration = ${videoRef.current.duration}`);
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canPlayThrough, timeline, setPosition, setCurrentTime, videoRef, stretchContainer, isMobile]);

  // setup scroll triggers
  useEffect(() => {

    // when the scroll trigger stretchContainer touches the top of the screen, start the scrollTrigger that controls all of the overlays and deepdives
    ScrollTrigger.create({
      trigger: stretchContainer.current,
      start: "top+=0px",
      onEnter: hideIntro,
      onLeaveBack: showIntro
    });

    ScrollTrigger.create({
      trigger: outroRef.current,
      start: "top-=100%",
      bottom: "top+=9999999px",
      onEnter: () => {
        showEndFrame();
      },
      onLeaveBack: () => {
        hideEndFrame();
      },
    });

    ScrollTrigger.create({
      trigger: introScreenRef.current.querySelector("#IntroScreen"),
      start: "top+=0px",
      bottom: "top+=9999999px",
      onLeaveBack: () => {
        setCurrentOverlay("intro");
      },
    });

    ScrollTrigger.create({
      trigger: introScreenRef.current.querySelector("#helpScreen"),
      start: "top+=0px",
      bottom: "top+=9999999px",
      onEnter: () => {
        gsap.killTweensOf(beginRef.current);
        CrossFade(beginRef.current, continueRef.current);
        setCurrentOverlay("help");
      },
      onLeaveBack: CrossFade(continueRef.current, beginRef.current),
    });
    
    // intro animation
    gsap.from([introScreenRef.current.querySelector(".introScreenLogo"), introScreenRef.current.querySelector(".introScreenCopy"), introScreenRef.current.querySelector(".introScreenCallout"), beginRef.current], {
      opacity: 0,
      duration: 2,
      ease: "power3.inOut",
      stagger: .06,
      delay: .25
    });

    // start experience at top of page and fade in bg video and play it
    setTimeout(() => {
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
      showIntro();
    }, 50);

    document.body.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') {
            document.body.classList.add('user-is-tabbing');
            document.body.classList.remove('user-is-not-tabbing');
        }
    });
    
    document.body.addEventListener('mousedown', () => {
        document.body.classList.add('user-is-not-tabbing');
        document.body.classList.remove('user-is-tabbing');
    });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showIntro = () => {

    gsap.to( videoRef.current, {opacity: 0, duration: .75, ease: "power3.out"});
    if (continueRef.current) {
        gsap.to( continueRef.current.querySelector("div span"), {opacity: 1, duration: .75, ease: "power3.out"});
    }
    gsap.to( overlaysRef.current, {opacity: 0, duration: .75, ease: "power3.out"});
    gsap.to( headerRef.current, {opacity: 0, duration: .75, ease: "power3.out"});
    
    gsap.to(introBgRef.current, {
      opacity: 1,
      duration: 1,
      ease: "power3.inOut",
    });

    if (typeof window.showVideo === "function") {
      window.showVideo(false);
    } else {
      console.error(`\t window.showVideo is not defined`);
    }
    setPlayIntroVideo(true);
    setCurrentOverlay(null);
  };

  const hideIntro = () => {

    gsap.to( beginRef.current, {opacity: 0, duration: .75, ease: "power3.out"});
    gsap.to( videoRef.current, {opacity: 1, duration: .75, ease: "power3.out",
      onComplete: () => {
        gsap.to(introBgRef.current, { opacity: 0, duration: .75, ease: "power2.out" });
        setPlayIntroVideo(false);
      }
    });
    try { gsap.to( continueRef.current.querySelector("div span"), {opacity: 0, duration: .75, ease: "power3.out"}); } catch (e) {}
    gsap.to( overlaysRef.current, {opacity: 1, duration: .75, ease: "power3.out"});
    gsap.to( headerRef.current, {opacity: 1, duration: .75, ease: "power3.out", delay: .25});

    if (typeof window.showVideo === "function") {
      window.showVideo(true);
    } else {
      console.error(`\t window.showVideo is not defined`);
    }
    
    setCurrentOverlay("overlay_0");
  };

  const showEndFrame = () => {
    setCurrentOverlay(`outro`);
    setPlayEndFrame(true);
    gsap.to( [videoRef.current], {opacity: 0, duration: .75, delay: .5, ease: "power3.out"});
  };

  const hideEndFrame = () => {
    setCurrentOverlay(null);
    setPlayEndFrame(false);
    gsap.to( [videoRef.current], {opacity: 1, duration: .75, ease: "power3.out"});
  };

  return (
    <div id="VideoContainer" className={`h-auto w-full m-0 p-0`}>
      
      <DebugUtils ref={debugRef} />

      <IntroBackground ref={introBgRef} playVideo={playIntroVideo} className={`${isLoading ? "invisible" : "visible"}`} />
      <Intro ref={introScreenRef} />

      <Header ref={headerRef} />

      <ScrollButton ref={beginRef} id="scrollToBegin" className={`opacity-100 ${isLoading ? "!hidden" : "block"}`} text="Scroll to begin" />      
      <ScrollButton ref={continueRef} id="scrollToContinue" className={`${isLoading ? "!hidden" : "block"}`} text="Scroll to continue" />

      <Overlays ref={overlaysRef} totalTime={totalTime} debugRef={debugRef} />
      <DeepDives ref={deepdivesRef} />

      <VoiceOver />

      <div id="Stretcher" ref={stretchContainer} className={`pointer-events-none h-[27000px] lg:h-[35000px]`}></div>
      
      <Outro ref={outroRef} playVideo={playEndFrame} />

      {
        (isMobile !== null && isMobile === 1) ?
        <video 
          ref={videoRef} 
          src="./vids/mobile.mp4"
          className={`video-background fixed opacity-0`}
          playsInline={true}
          preload="auto"
          muted="muted"
          autoPlay
          type="video/mp4"/>
        :
        <video 
          ref={videoRef} 
          src="./vids/desktop.mp4"
          className={`video-background fixed opacity-0`}
          playsInline={true}
          preload="auto"
          muted="muted"
          autoPlay
          type="video/mp4"/>
      }

    </div>
  );
}

export default Video;

